"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";

// 标题布局类型：center 居中 | left 左侧带横线
export type TitleLayout = "center" | "left";

export interface SolutionProps {
	// 翻译命名空间
	translationNamespace?: string;
	// 自定义标题（第一行，优先级高于翻译）
	titleLine1?: string;
	// 自定义标题（第二行，优先级高于翻译）
	titleLine2?: string;
	// 标题布局：center 居中 | left 左侧带横线
	titleLayout?: TitleLayout;
	// 左侧按钮跳转链接
	leftButtonLink?: string;
	// 右侧按钮跳转链接
	rightButtonLink?: string;
}

const Solution = ({
	translationNamespace = "solution",
	titleLine1,
	titleLine2,
	titleLayout = "center",
	leftButtonLink = "/rapid-prototyping",
	rightButtonLink = "/upload",
}: SolutionProps) => {
	const t = useTranslations(translationNamespace);

	// 获取显示的标题
	const displayTitleLine1 = titleLine1 || t("titleLine1");
	const displayTitleLine2 = titleLine2 || t("titleLine2");
	// 兼容旧的单行标题
	const displayTitle = t("title");

	return (
		<section className="relative py-8 sm:py-10 md:py-12 lg:py-16 overflow-hidden">
			{/* 左侧圆点背景装饰 - 带边缘模糊 */}
			<div className="absolute left-0 top-1/2 -translate-y-1/2 w-[30%] h-full pointer-events-none">
				<div
					className="absolute inset-0 opacity-50"
					style={{
						backgroundImage: `radial-gradient(circle, #787878 1.5px, transparent 1.5px)`,
						backgroundSize: "16px 16px",
						maskImage: `linear-gradient(to right, white 0%, white 50%, transparent 100%), 
									linear-gradient(to bottom, transparent 0%, white 15%, white 85%, transparent 100%)`,
						WebkitMaskImage: `linear-gradient(to right, white 0%, white 50%, transparent 100%), 
										  linear-gradient(to bottom, transparent 0%, white 15%, white 85%, transparent 100%)`,
						maskComposite: "intersect",
						WebkitMaskComposite: "source-in",
					}}
				/>
			</div>

			<div className="container relative z-10">
				{/* 主标题 - 支持两种布局 */}
				{titleLayout === "center" ? (
					<h2 className="text-center text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl mb-6 sm:mb-8 md:mb-10 lg:mb-12">
						{displayTitle}
					</h2>
				) : (
					<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center">
						<div className="inline-flex items-end justify-center gap-4">
							<span className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
								{displayTitleLine1}
							</span>
							<div className="mb-1.5 h-1 w-12 flex-shrink-0 bg-themePrimary400 sm:mb-2 md:w-24" />
						</div>
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl mt-1">
							{displayTitleLine2}
						</h2>
					</div>
				)}

				{/* 卡片容器 */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
					{/* 左侧卡片 - 白色背景 */}
					<div className="bg-[#f5f5f5] rounded p-6 md:p-8 lg:p-10">
					<span className="text-lg text-gray-900 tracking-wider uppercase">
						{t("leftTag")}
					</span>
					<h3 className="mt-2 text-lg font-bold sm:text-xl md:text-2xl text-black">
						{t("leftTitle")}
					</h3>
					<p className="mt-4 text-sm leading-relaxed md:text-base text-black">
							{t("leftDescription")}
						</p>
						<Link
							href={leftButtonLink}
							className="mt-6 inline-block hover:text-white rounded-md bg-[#6fa32b] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-opacity-80"
						>
							{t("leftButtonText")}
						</Link>
					</div>

					{/* 右侧卡片 - 浅绿色背景 */}
					<div className="bg-[#f0f9e8] rounded-lg p-6 md:p-8 lg:p-10">
					<span className="text-lg text-gray-900 tracking-wider uppercase">
						{t("rightTag")}
					</span>
					<h3 className="mt-2 text-lg font-bold sm:text-xl md:text-2xl text-black">
						{t("rightTitle")}
					</h3>
					<p className="mt-4 text-sm leading-relaxed md:text-base text-black">
							{t("rightDescription")}
						</p>
						<Link
							href={rightButtonLink}
							className="mt-6 inline-block hover:text-white rounded-md bg-[#6fa32b] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-opacity-80"
						>
							{t("rightButtonText")}
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Solution;
